'use client';
import { useSyncExternalStore } from 'react';
import Bookmark from '../../../public/icons/bookmark.svg';
import type { GreenhouseJob } from '@/libs/greenhouse/greenhouse';
import { getSavedJobIds, setSavedJobsCookie } from '@/libs/greenhouse/greenhouse-functions';

const listeners = new Set<() => void>();

export const subscribe = (callback: () => void) => {
  listeners.add(callback);
  return () => listeners.delete(callback);
};

export const notifyListeners = () => listeners.forEach(l => l());

const SaveJobButton = ({ job }: { job: GreenhouseJob }) => {
  const isSaved = useSyncExternalStore(
    subscribe,
    () => getSavedJobIds().includes(job.id),
    () => false,
  );

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const savedIds = getSavedJobIds();
    const alreadySaved = savedIds.includes(job.id);
    const updatedIds = alreadySaved ? savedIds.filter(id => id !== job.id) : [...savedIds, job.id];

    setSavedJobsCookie(updatedIds);
    notifyListeners();
  };

  return (
    <button
      className="group hover:cursor-pointer relative z-2 w-10 h-10 flex items-center justify-center"
      type="button"
      onClick={handleClick}
      aria-pressed={isSaved}>
      {isSaved ? (
        <Bookmark className="fill-[#1E1E1E] stroke-2 hover:fill-none transition-colors duration-300" />
      ) : (
        <Bookmark className="fill-none stroke-2 group-hover:fill-[#1E1E1E] transition-colors duration-300" />
      )}
      <span className="sr-only">
        {isSaved ? 'saved' : 'bookmark'} {job.title}
      </span>
    </button>
  );
};

export default SaveJobButton;
